
import React, { useState, useEffect } from 'react';
import Typography from '../../components/Typography/Typography';
import Button from '../../components/Button/Button';
import AddAzureUser from '../../dialogs/AddAzureUser/AddAzureUser';
import EditAzureUser from '../../dialogs/EditAzureUser/EditAzureUser';
import DeleteAzureUser from '../../dialogs/DeleteAzureUser/DeleteAzureUser';
import AzurePagination from '../../components/Pagination/AzurePagination';
import DataTable, { dataTableRowClass, dataTableTdClass, dataTableThClass, searchInputClass, TABLE_HEIGHT } from '../../components/DataTable/DataTable';
import { FiUsers } from 'react-icons/fi';
import { ClipLoader } from 'react-spinners';
import ASSETS from '../../assets';
import toast from 'react-hot-toast';
import { authAPI, AzureUser, AzureUsersResponse, SearchAzureUserResponse } from '../../services/api';

const Azure = () => {
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AzureUser | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState<AzureUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [skipToken, setSkipToken] = useState<string>('');
  const [total, setTotal] = useState(0);
  const [pageSize] = useState(10);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch Azure users
  const fetchUsers = async (page: number = 1, search: string = '', currentSkipToken: string = '') => {
    try {
      setLoading(true);
      setError(null);
      
      if (search && search.trim()) {
        // Search API call
        const searchResponse: SearchAzureUserResponse[] = await authAPI.searchAzureUser({ input: search.trim() });
        
        // Convert search response to AzureUser format
        const convertedUsers: AzureUser[] = searchResponse.map(user => ({
          firstName: user.givenName,
          lastName: user.surname,
          displayName: user.displayName
        }));
        
        setUsers(convertedUsers);
        setTotal(convertedUsers.length);
        setSkipToken('');
      } else {
        // Regular pagination API call
        // Only include skipToken if it exists and is not empty
        const requestPayload: any = { pageSize };
        if (currentSkipToken && currentSkipToken.trim()) {
          requestPayload.skipToken = currentSkipToken;
        }
        
        const response: AzureUsersResponse = await authAPI.getAzureUsers(requestPayload);
        
        // Always replace users for pagination (don't append)
        setUsers(response.members);
        setTotal(response.total);
        setSkipToken(response.skipToken || '');
      }
      
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to fetch Azure users';
      setError(errorMessage);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchUsers(1, searchTerm, '');
  }, []);

  // Search with debounce
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      setCurrentPage(1);
      setSkipToken('');
      fetchUsers(1, searchTerm, '');
    }, 500);

    setSearchTimeout(timeout);

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [searchTerm]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    if (page === 1) {
      // Go to page 1 - reset and fetch first page
      setSkipToken('');
      fetchUsers(1, searchTerm, '');
    } else if (page > currentPage && skipToken) {
      // Load next page using skipToken
      fetchUsers(page, searchTerm, skipToken);
    } else if (page < currentPage) {
      // For previous pages, we need to reset and fetch from beginning
      // This is a limitation of skipToken pagination - we can't go back easily
      setSkipToken('');
      fetchUsers(1, searchTerm, '');
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
    setSkipToken('');
  };

  // Refresh Azure users data
  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      setCurrentPage(1);
      setSkipToken('');
      setSearchTerm('');
      setError(null);
      
      // Clear any existing search timeout
      if (searchTimeout) {
        clearTimeout(searchTimeout);
        setSearchTimeout(null);
      }
      
      await fetchUsers(1, '', '');
      toast.success('Azure users refreshed successfully!');
    } catch (err: any) {
      console.error('Error refreshing Azure users:', err);
      toast.error('Failed to refresh Azure users. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleEditUser = (user: AzureUser) => {
    setSelectedUser(user);
    setIsEditUserModalOpen(true);
  };

  const handleDeleteUser = (user: AzureUser) => {
    setSelectedUser(user);
    setIsDeleteUserModalOpen(true);
  };

  const handleAddUserSuccess = () => {
    fetchUsers(1, searchTerm, '');
  };

  const handleEditUserSuccess = () => {
    fetchUsers(1, searchTerm, '');
  };

  const handleDeleteUserSuccess = () => {
    fetchUsers(1, searchTerm, '');
  };

  const hasNextPage = Boolean(skipToken && skipToken.trim() !== '');

  return (
    <div className='flex-grow bg-white  rounded-[15px] flex flex-col'>

  
      <div className='  rounded-lg'>
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <div className="flex items-center gap-3">
            <Typography variant="h2" className="font-bold title text-xl sm:text-2xl lg:text-2xl">
              Azure Users
            </Typography>
            
            {/* Refresh Button */}
            <div className="relative group">
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className={`p-2 rounded-full transition-all duration-200 ${
                  isRefreshing 
                    ? 'bg-blue-100 cursor-not-allowed' 
                    : 'bg-gray-100 hover:bg-blue-100 hover:text-blue-600'
                }`}
                title="Refresh Azure users list"
              >
                <svg 
                  className={`w-4 h-4 transition-transform duration-200 ${
                    isRefreshing ? 'animate-spin text-blue-600' : 'text-gray-600'
                  }`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                  />
                </svg>
              </button>
              
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                Azure takes time to load the data
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">

            {/* Search Bar */}
            <div className="relative flex-1 sm:flex-none sm:w-80">
              <input
                type="text"
                placeholder="Search by name & email..."
                value={searchTerm}
                onChange={handleSearchChange}
                className={searchInputClass}
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 gap-2">
                {/* Clear Button */}
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="p-1 hover:bg-gray-100 rounded-full transition-colors duration-200"
                    title="Clear search"
                  >
                    <img src={ASSETS.cross} alt="Clear" className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
                {/* Search Icon */}
                {ASSETS.search && (
                  <img src={ASSETS.search} alt="Search" className="w-4 h-4 text-gray-400" />
                )}
              </div>
            </div>
            
            {/* Add User Button */}
            <Button
              label="Add User"
              variant="primary"
              onClick={() => setIsAddUserModalOpen(true)}
              className="w-full sm:w-auto flex items-center gap-1 !h-[38px]"
              leftIcon={ASSETS.add}
            />
          </div>
        </div>

        {/* Table Container */}
        <DataTable
          footer={
            !loading && !error && users?.length > 0 ? (
              <AzurePagination
                currentPage={currentPage}
                onPageChange={handlePageChange}
                totalItems={total}
                itemsPerPage={pageSize}
                hasNextPage={hasNextPage}
                isLoading={loading}
                embedded
              />
            ) : undefined
          }
        >
            <table className="w-full border-collapse">
              <thead className="sticky top-0 z-10 shadow-[0_1px_0_0_rgba(226,232,240,1)]">
                <tr>
                  <th className={`${dataTableThClass} text-center min-w-[40px]`}>
                    Sr.
                  </th>
                  <th className={`${dataTableThClass} text-left min-w-[130px]`}>
                    First Name
                  </th>
                  <th className={`${dataTableThClass} text-left min-w-[130px]`}>
                    Last Name
                  </th>
                  <th className={`${dataTableThClass} text-left min-w-[210px]`}>
                    Email
                  </th>
                  <th className={`${dataTableThClass} text-center min-w-[130px]`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="text-center align-middle" style={{ height: TABLE_HEIGHT - 48 }}>
                      <div className="flex flex-col items-center justify-center gap-3 h-full">
                        <ClipLoader size={40} color="#3B82F6" />
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={5} className="text-center align-middle" style={{ height: TABLE_HEIGHT - 48 }}>
                      <div className="flex flex-col items-center justify-center gap-3 h-full">
                        <div className="p-3 bg-red-100 rounded-full">
                          <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <Typography variant="h3" className="text-red-500 font-medium">
                          Error loading users
                        </Typography>
                        <Typography variant="body1" className="text-gray-400">
                          {error}
                        </Typography>
                        <Button
                          label="Retry"
                          variant="outlined"
                          onClick={() => fetchUsers(1, searchTerm, '')}
                          className="mt-2"
                        />
                      </div>
                    </td>
                  </tr>
                ) : users?.length > 0 ? (
                  users?.map((user, index) => (
                    <tr key={`${user.displayName}-${index}`} className={dataTableRowClass}>
                      <td className={`${dataTableTdClass} text-center min-w-[40px] font-medium text-slate-500`}>
                        {index + 1}
                      </td>
                      <td className={`${dataTableTdClass} min-w-[130px]`}>
                        {user.firstName}
                      </td>
                      <td className={`${dataTableTdClass} min-w-[130px]`}>
                        {user.lastName}
                      </td>
                      <td className={`${dataTableTdClass} min-w-[210px]`}>
                        {user.displayName}
                      </td>
                      <td className={`${dataTableTdClass} text-center min-w-[130px]`}>
                        <div className="flex justify-center gap-6">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="p-1 hover:bg-blue-50 rounded-full transition-colors duration-200"
                            title="Edit user"
                          >
                            <img src={ASSETS.edit} alt="edit" className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user)}
                            className="p-1 hover:bg-red-50 rounded-full transition-colors duration-200"
                            title="Delete user"
                          >
                            <img src={ASSETS.delete} alt="delete" className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center align-middle" style={{ height: TABLE_HEIGHT - 48 }}>
                      <div className="flex flex-col items-center justify-center gap-3 h-full">
                        <div className="p-3 bg-gray-100 rounded-full">
                          {FiUsers({ className: "w-8 h-8 text-gray-400" })}
                        </div>
                        <Typography variant="h3" className="text-gray-500 font-medium">
                          {searchTerm ? 'No users found' : 'No users available'}
                        </Typography>
                        <Typography variant="body1" className="text-gray-400">
                          {searchTerm 
                            ? `No users match "${searchTerm}". Try adjusting your search terms.`
                            : 'No users have been added yet. Click "Add User" to get started.'
                          }
                        </Typography>
                        {searchTerm && (
                          <button
                            onClick={handleClearSearch}
                            className="mt-2 px-4 py-2 text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200"
                          >
                            Clear search
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
        </DataTable>
      </div>

      {/* Add User Modal */}
      <AddAzureUser
        open={isAddUserModalOpen}
        setOpen={setIsAddUserModalOpen}
        onSuccess={handleAddUserSuccess}
      />

      {/* Edit User Modal */}
      <EditAzureUser
        open={isEditUserModalOpen}
        setOpen={setIsEditUserModalOpen}
        user={selectedUser}
        onSuccess={handleEditUserSuccess}
      />

      {/* Delete User Modal */}
      <DeleteAzureUser
        open={isDeleteUserModalOpen}
        setOpen={setIsDeleteUserModalOpen}
        user={selectedUser}
        onSuccess={handleDeleteUserSuccess}
      />
    </div>
  );
};

export default Azure;
